/**
 * The rover's own panel: the auto-survey toggle, jump-to-rover's camera ease,
 * and the instrument readout drawn from the measured ground and the drive.
 */

import type { FocusRigHandle } from '../focusRig';
import type { PlotGeometryHandle } from '../plotGeometry';
import type { RoverDriveHandle } from '../roverDrive';
import type { SolarScrubHandle } from '../solarScrub';
import type { CameraRigHandle } from '../cameraRig';
import type { AtlasLive } from '../../atlas';

export interface TelemetryPanelDeps {
  readonly focusRig: FocusRigHandle;
  readonly plotGeometry: PlotGeometryHandle;
  readonly roverDrive: RoverDriveHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly live: AtlasLive;
  /** Built after this module, so atlas.ts hands it over through a getter. */
  readonly cameraRig: CameraRigHandle;
  readonly overlay: HTMLElement;
}

export interface TelemetryPanelHandle {
  readonly autoRow: HTMLElement;
  readonly jumpRow: HTMLElement;
  readonly telemEl: HTMLElement;
  readonly autoBtn: HTMLButtonElement;
  renderTelemetry(): void;
  /** Declared here and written from inside this seam, but read and written
   *  from the rover-deploy code above this seam's call site in atlas.ts too;
   *  a plain accessor pair so either side sees the other's write. */
  auto: boolean;
  telemAccum: number;
}

export function createTelemetryPanel(deps: TelemetryPanelDeps): TelemetryPanelHandle {
  const { overlay } = deps;
  const { solar } = deps.solarScrub;
  const { focusCam, altitude } = deps.focusRig;
  const { subPlotAt } = deps.plotGeometry;
  const { jumpEaseFromLook } = deps.roverDrive;

  // ── auto survey + telemetry ─────────────────────────────────────────────
  let auto = false;
  const autoBtn = overlay.querySelector('#pc-auto') as HTMLButtonElement;
  const autoRow = overlay.querySelector('#pc-auto-row') as HTMLElement;
  const telemEl = overlay.querySelector('#atlas-telem') as HTMLElement;

  // ── jump to rover ───────────────────────────────────────────────────────
  //
  // The path back onto the rover once re-entry has left the camera on the
  // anchor: a press, not a cut. It eases the same 12 m behind-and-above frame
  // deploy uses, over `JUMP_EASE_S`, rather than copying the rover's position
  // into the look-at in one drawn frame the way the whip did.
  const jumpRow = overlay.querySelector('#pc-jump-row') as HTMLElement;
  const jumpBtn = overlay.querySelector('#pc-jump-rover') as HTMLButtonElement;
  jumpBtn.addEventListener('click', () => {
    if (!deps.roverDrive.state.driving || !deps.live.plot?.rover) return;
    jumpEaseFromLook.copy(deps.cameraRig.orbit.lookAt);
    deps.roverDrive.state.jumpEaseFromYaw = focusCam.state.yaw;
    deps.roverDrive.state.jumpEaseFromPitch = focusCam.state.pitch;
    deps.roverDrive.state.jumpEaseFromTilt = deps.cameraRig.plotTilt;
    deps.roverDrive.state.jumpEaseFromAlt = altitude();
    deps.roverDrive.state.jumpEaseT = 0;
    deps.roverDrive.state.followRover = true;
    deps.live.needsRender = true;
  });

  autoBtn.addEventListener('click', () => {
    auto = !auto;
    autoBtn.classList.toggle('on', auto);
    autoBtn.innerHTML = auto ? '&#9632; STOP SURVEY' : '&#9654; AUTO SURVEY';
    deps.live.needsRender = true;
  });

  let telemAccum = 0;
  /**
   * Instrument readout.
   *
   * Most of this is REAL — the slope, elevation, insolation and rock abundance
   * come off the same measured field everything else reads, and the odometer is
   * integrated from the drive. Only the power and thermal lines are invented,
   * and they are labelled so nobody mistakes a prop for an instrument.
   */
  function renderTelemetry(): void {
    const r = deps.live.plot?.rover;
    if (!r || !deps.live.plotField) { telemEl.classList.add('hidden'); return; }
    telemEl.classList.remove('hidden');
    const st = r.state;
    const elevM = deps.live.plotField.heightAt(st.x, st.z);
    const sub = subPlotAt(st.x, st.z);
    const rk = deps.live.plotField.meta.rocks;
    // Invented, and flagged as such: a real MMRTG puts out ~110 W at BOL and
    // decays a few percent a year. The sol phase drives the thermal swing.
    const rtgW = 108 - Math.abs(Math.sin(solar.solPhase * Math.PI * 2)) * 3.5;
    const tempC = -62 + Math.sin((solar.solPhase - 0.28) * Math.PI * 2) * 34;
    const row = (k: string, v: string, cls = '') =>
      `<div class="tl-row"><span>${k}</span><b class="${cls}">${v}</b></div>`;
    telemEl.innerHTML =
      `<div class="tl-head">ROVER · TELEMETRY</div>`
      + row('MODE', auto ? 'AUTO SURVEY' : 'MANUAL', auto ? 'tl-ok' : '')
      + row('SPEED', `${Math.abs(st.speed).toFixed(2)} m/s`)
      + row('ODOMETER', st.speed || r.odometerM ? `${r.odometerM.toFixed(1)} m` : '0.0 m')
      + row('HEADING', `${(((((st.yaw * 180) / Math.PI) % 360) + 360) % 360).toFixed(0)}°`)
      + `<div class="tl-sep">GROUND · measured</div>`
      + row('ELEVATION', `${elevM.toFixed(1)} m`)
      + row('SLOPE', `${st.slopeDeg.toFixed(1)}°`, st.slipping ? 'tl-warn' : '')
      + row('WHEELS', r.onRock ? 'ON ROCK' : 'ON REGOLITH', r.onRock ? 'tl-ok' : '')
      + row('TRACTION', st.slipping ? 'SLIP' : 'NOMINAL', st.slipping ? 'tl-warn' : 'tl-ok')
      + (sub ? row('CELL', `${sub.verdict.toUpperCase()} · ${sub.score.toFixed(0)}`) : '')
      + (sub ? row('INSOLATION', `${sub.insolationWhSol.toFixed(0)} Wh/m²·sol`) : '')
      + (rk ? row('ROCK k', `${rk.k_used.toFixed(3)} · ${rk.k_source}`) : '')
      + `<div class="tl-sep">BUS · simulated</div>`
      + row('MMRTG', `${rtgW.toFixed(0)} W`)
      + row('CHASSIS', `${tempC.toFixed(0)} °C`)
      + row('LINK', 'MRO · next pass 04:12');
  }

  return {
    autoRow, jumpRow, telemEl, autoBtn, renderTelemetry,
    get auto() { return auto; },
    set auto(v: boolean) { auto = v; },
    get telemAccum() { return telemAccum; },
    set telemAccum(v: number) { telemAccum = v; },
  };
}
